// Original growing guides written for this site. Where useful, we link
// out to specialist sites (Farmer Gracy, David Austin Roses, RHS) as
// further reading - no text or images are copied from those sites.
//
// To add a new guide: append an object to this array with a unique
// slug, then it appears automatically in the Guides index and at
// /guides/{slug}.

const guides = [
  {
    slug: 'starting-seeds-tubers-indoors',
    title: 'Starting Seeds and Tubers Indoors',
    icon: '🌱',
    summary: 'How to get a head start on the season indoors, then move plants outside safely once the weather warms up.',
    category: 'Techniques',
    sections: [
      {
        heading: 'Why start indoors',
        body: [
          'Starting tender plants indoors gives them a head start before the UK\u2019s unpredictable spring frosts have passed, and it means plants like dahlias, sweet peas and tomatoes are already flowering or fruiting size by the time they go outside.',
          'It also lets you control conditions precisely - temperature, light and water - during the most vulnerable stage of a plant\u2019s life, when a single cold night or dry spell can kill a seedling outright.',
        ],
      },
      {
        heading: 'Temperature and heat',
        body: [
          'Most vegetable and flower seeds germinate best between 18-21°C. A heated propagator or a heat mat under trays keeps the compost at a steady temperature, which speeds up and evens out germination compared to relying on room temperature alone.',
          'Once seedlings emerge, most can drop to a slightly cooler 15-18°C - too warm and they grow leggy and weak as they stretch for light.',
        ],
      },
      {
        heading: 'Grow lights',
        body: [
          'A sunny windowsill is rarely bright enough on its own in February and March - seedlings will lean and stretch towards the glass. A simple LED grow light on a timer (12-16 hours on) positioned 15-30cm above the seedlings keeps growth compact and sturdy.',
          'Raise the light as plants grow so it stays a consistent distance above the leaves rather than scorching the tops.',
        ],
      },
      {
        heading: 'Watering',
        body: [
          'Water from below where possible (standing trays in a shallow tray of water for 10-15 minutes) to avoid disturbing fine roots or compacting the compost surface.',
          'Keep compost consistently moist but never waterlogged - allow the surface to dry slightly between waterings once seedlings are established, since constantly wet compost encourages damping-off disease.',
        ],
      },
      {
        heading: 'Hardening off',
        body: [
          'Before planting outside permanently, acclimatise plants gradually over 7-14 days - this is the single most common place seedlings are lost if skipped.',
          'Start by putting trays outside in a sheltered spot for a few hours on a mild day, bringing them back in at night. Gradually increase the time outside each day, then leave them out overnight once you\u2019re confident there\u2019s no frost risk, before finally planting into their permanent positions.',
          'Watch the forecast closely - a late frost can undo weeks of careful raising in a single night.',
        ],
      },
    ],
    furtherReading: [
      { label: 'Farmer Gracy - bulb and tuber growing advice', url: 'https://www.farmergracy.co.uk/' },
      { label: 'RHS - sowing seeds', url: 'https://www.rhs.org.uk/propagation/sowing-seeds' },
    ],
  },
  {
    slug: 'pruning-roses',
    title: 'Pruning Roses',
    icon: '🌹',
    summary: 'When and how to prune bush, shrub and climbing roses for strong growth and better flowering.',
    category: 'Pruning',
    sections: [
      {
        heading: 'When to prune',
        body: [
          'The main prune for bush and shrub roses is in late winter (February in most of the UK), just as growth buds start to swell but before strong new growth begins.',
          'Climbing and rambling roses are usually pruned after flowering, since many climbers flower on the previous year\u2019s growth - pruning them in winter like a bush rose can remove the coming season\u2019s flowers.',
        ],
      },
      {
        heading: 'Basic technique',
        body: [
          'Start by removing the "three Ds" - dead, damaged and diseased wood - cutting back to healthy, white/cream pith inside the stem.',
          'Cut at a slight angle, about 5mm above an outward-facing bud, so new growth is encouraged to grow away from the centre of the plant rather than crossing through it.',
          'Aim to open up the centre of the bush for airflow, which reduces fungal problems like blackspot.',
        ],
      },
      {
        heading: 'How hard to cut',
        body: [
          'Bush roses (hybrid teas, floribundas) are usually cut back hard, to around 15-25cm from the ground, for the biggest and best-quality blooms.',
          'Shrub roses need a lighter touch - remove around a third of old growth and shape rather than cutting hard, to preserve their natural habit.',
          'Climbers: tie in the main framework of stems horizontally where possible (this encourages more flowering side-shoots), and trim the side-shoots back to 2-3 buds after flowering.',
        ],
      },
      {
        heading: 'Aftercare',
        body: [
          'Feed with a rose fertiliser and mulch with well-rotted manure or compost after pruning to fuel the new season\u2019s growth.',
          'Clear away and dispose of prunings and fallen leaves rather than composting them if blackspot or rust was present, to reduce reinfection.',
        ],
      },
    ],
    furtherReading: [
      { label: 'David Austin Roses - rose care and pruning advice', url: 'https://www.davidaustinroses.co.uk/' },
      { label: 'RHS - pruning roses', url: 'https://www.rhs.org.uk/roses/pruning' },
    ],
  },
  {
    slug: 'pruning-fruit-trees',
    title: 'Pruning Fruit Trees',
    icon: '🍎',
    summary: 'Winter pruning for apples and pears versus summer pruning for stone fruit, and why the timing matters.',
    category: 'Pruning',
    sections: [
      {
        heading: 'Apples and pears - winter pruning',
        body: [
          'Prune apples and pears while dormant, between November and early March, ideally on a dry, frost-free day.',
          'Remove dead, damaged, crossing or inward-growing branches first, then thin congested spurs (the short stubby growths that carry fruit buds) to let light and air into the centre of the tree.',
          'For young trees, focus on building a strong, open framework of well-spaced branches rather than heavy fruiting - formative pruning in the first few years shapes the tree for decades of cropping.',
        ],
      },
      {
        heading: 'Stone fruit - summer pruning only',
        body: [
          'Plums, cherries, apricots and peaches should NOT be pruned in winter - open wounds in cold, wet weather are the main entry point for silver leaf disease, which can kill the tree.',
          'Prune these in summer instead (June-August), while the tree is in full leaf and healing quickly, removing dead, damaged or crossing wood and any strong upright shoots spoiling the shape.',
        ],
      },
      {
        heading: 'Restricted forms (espalier, cordon, fan)',
        body: [
          'Trained fruit trees need more frequent, lighter pruning to maintain their shape - typically summer pruning to control new growth and keep the trained framework tidy, rather than one big winter cut.',
          'Cordons and espaliers are usually pruned in late summer, cutting new side shoots back to 2-3 leaves above the basal cluster once they\u2019ve firmed up at the base.',
        ],
      },
      {
        heading: 'General technique',
        body: [
          'Always cut just above a bud, angled away from it so water runs off rather than pooling on the cut face.',
          'Remove no more than about a quarter of the canopy in any one session - heavy pruning in a single year can trigger excessive vigorous regrowth at the expense of fruiting.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - pruning fruit trees', url: 'https://www.rhs.org.uk/fruit/pruning-fruit-trees' },
    ],
  },
  {
    slug: 'spring-flowering-bulbs',
    title: 'When to Plant Spring-Flowering Bulbs',
    icon: '🌷',
    summary: 'Planting windows and depth guidance for daffodils, tulips, crocus, alliums and other spring bulbs.',
    category: 'Bulbs',
    sections: [
      {
        heading: 'Planting windows',
        body: [
          'Most spring-flowering bulbs are planted in autumn, while the soil is still workable but has cooled down from summer - this gives roots time to establish before the ground gets cold.',
          'Daffodils (narcissi) and crocus are best planted early, from September, so they can put down roots well before winter.',
          'Tulips are the exception - plant them later, in November or even into early December, once the soil has cooled. Planting tulips too early increases the risk of tulip fire, a fungal disease that thrives in warmer, damper soil.',
          'Alliums and hyacinths sit in between - October is a good general target.',
        ],
      },
      {
        heading: 'Depth and spacing',
        body: [
          'A reliable rule of thumb is to plant a bulb about three times its own height deep, measured from the base of the bulb.',
          'Space bulbs roughly two to three bulb-widths apart for a natural-looking drift, or closer together in containers for a fuller display.',
        ],
      },
      {
        heading: 'Site and soil',
        body: [
          'Good drainage matters more than soil richness - bulbs rot in waterlogged ground over winter. Add horticultural grit to heavy clay soil if needed, especially for tulips.',
          'Most spring bulbs want a sunny to lightly shaded spot; woodland bulbs like snowdrops and bluebells are the exception and prefer dappled shade under deciduous trees.',
        ],
      },
      {
        heading: 'Naturalising in lawns',
        body: [
          'For a naturalised look in grass, scatter bulbs by hand and plant them where they land rather than in rigid rows - this mimics how they\u2019d spread naturally.',
          'Leave the foliage to die back naturally after flowering (usually 6 weeks) before mowing - this is when the bulb is recharging itself for next year.',
        ],
      },
    ],
    furtherReading: [
      { label: 'Farmer Gracy - bulb planting guides', url: 'https://www.farmergracy.co.uk/' },
      { label: 'RHS - bulbs', url: 'https://www.rhs.org.uk/bulbs' },
    ],
  },
  {
    slug: 'growing-garlic',
    title: 'Growing Garlic: Planting and Harvest',
    icon: '🧄',
    summary: 'When to plant garlic cloves, and the signs that tell you it\u2019s ready to lift.',
    category: 'Vegetables',
    sections: [
      {
        heading: 'When to plant',
        body: [
          'Garlic is unusual among vegetables in that it needs a period of cold to trigger bulb formation (vernalisation), so most varieties are planted in autumn rather than spring.',
          'Autumn planting: mid-October to December, for the widest choice of varieties and the largest bulbs the following summer.',
          'Spring planting: if you missed the autumn window, "spring garlic" varieties can go in from February to March, though bulbs tend to be smaller.',
          'Avoid planting into waterlogged soil - garlic cloves rot easily if they sit wet over winter.',
        ],
      },
      {
        heading: 'Planting method',
        body: [
          'Split a bulb into individual cloves just before planting and use only the largest, healthiest ones - small cloves produce small bulbs.',
          'Plant cloves pointy-end up, about 2.5-5cm deep, with the tip just below the soil surface, spaced 15cm apart in rows 30cm apart.',
        ],
      },
      {
        heading: 'When to harvest',
        body: [
          'Garlic is usually ready between June and August, roughly 6-9 months after autumn planting.',
          'The main sign it\u2019s ready: the lower leaves turn yellow and start to die back while the upper leaves are still green - lift a test bulb to check the cloves have filled out and the skin has started to papery over.',
          'Don\u2019t leave garlic in the ground too long after this point - the bulbs can split open and won\u2019t store well.',
        ],
      },
      {
        heading: 'Curing and storage',
        body: [
          'After lifting, cure bulbs by drying them somewhere warm, dry and well-ventilated (out of direct sun) for 2-4 weeks, with the leaves still attached, until the outer skins are fully papery.',
          'Once cured, trim the roots and leaves (or plait softneck varieties for hanging) and store somewhere cool, dry and dark - well-cured garlic can keep for several months.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - growing garlic', url: 'https://www.rhs.org.uk/vegetables/garlic/grow-your-own' },
    ],
  },
  {
    slug: 'seed-saving',
    title: 'Seed Saving',
    icon: '🌻',
    summary: 'How to collect, dry and store your own seed from this year\u2019s plants, ready for next season.',
    category: 'Techniques',
    sections: [
      {
        heading: 'Which plants to save seed from',
        body: [
          'Plain, non-hybrid plants "come true" from seed, which means the new plants will look like the parent. Good ones to start with in a South of England garden are peas, beans, lettuce, tomatoes, and hardy annuals like calendula and nigella.',
          'Named varieties and F1 hybrids are usually not worth saving seed from. The new plants can turn out very different from the parent. Plain species plants work much better for this.',
          'Only take seed from healthy, strong plants. Weak or diseased plants often give weak seed.',
        ],
      },
      {
        heading: 'When and how to collect',
        body: [
          'Seed is usually ready about two months after flowering. Once it starts to ripen it can go quickly, so check on it often rather than just once.',
          'Pick on a dry day, once the seed pod or head has ripened but before it splits open on its own. In a mild South of England garden, check more often in warm, dry weather, as this speeds things up.',
          'A few plants, such as calendula, are best picked a little earlier, while the seed is still green. Most others do best if left to ripen fully on the plant first.',
        ],
      },
      {
        heading: 'Drying and cleaning',
        body: [
          'Spread the seed heads out somewhere warm, dry and airy, out of direct sun, until they are fully dry and papery. A greenhouse, shed or windowsill works well. In this region\u2019s damp summers, seed can stay wet for longer than you\u2019d expect, so give it time.',
          'Once dry, separate the seed from the pods and other bits. A gentle shake in a paper bag, or rubbing between your fingers, is usually enough.',
        ],
      },
      {
        heading: 'Storing your seed',
        body: [
          'Keep clean, fully dry seed in a paper envelope or small airtight tub. Write the plant name and year on it. Store it somewhere cool and dark, such as a drawer, away from central heating.',
          'Seed grows best when it is fresh, so try to use saved seed within a year or two rather than keeping it for a long time.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - seed: collecting and storing', url: 'https://www.rhs.org.uk/propagation/seed-collecting-storing/' },
    ],
  },
  {
    slug: 'garden-tool-care',
    title: 'Garden Tool Care',
    icon: '🛠️',
    summary: 'How to clean, disinfect and sharpen secateurs and other hand tools, and why it matters.',
    category: 'Techniques',
    sections: [
      {
        heading: 'Why clean tools matter',
        body: [
          'A pruning cut is a fresh wound on the plant. A dirty blade can carry fungus, bacteria and sap straight from a sick plant into a healthy one. Rose black spot and other plant diseases spread this way just as easily as by wind and rain.',
          'Clean tools also last longer and cut better. A clean, sharp blade heals a cut faster than a dirty, blunt one that crushes the stem.',
        ],
      },
      {
        heading: 'Cleaning after use',
        body: [
          'Wipe off sap, soil and plant bits after every use. A stiff brush or a cloth is usually enough.',
          'If you are cutting anything that might be diseased, such as black spot on roses, wipe the blade with disinfectant or rubbing alcohol between cuts, not just at the end. This stops you spreading the disease to the next healthy plant.',
        ],
      },
      {
        heading: 'Rust and sharpening',
        body: [
          'Light rust can usually be rubbed off with fine wire wool and a little oil. Dry tools well before you put them away. In this region\u2019s damp weather, tools left in a cold shed rust faster than tools kept dry.',
          'A sharp blade is safer than a blunt one, as it needs less force and is less likely to slip. You can sharpen secateurs with a simple whetstone, following the angle the blade already has.',
        ],
      },
      {
        heading: 'Looking after tools at the end of the season',
        body: [
          'Before putting tools away for winter, clean them well, sharpen the blades, and wipe metal parts with a little oil to stop rust over the cold, damp months.',
          'Check wooden handles for splinters, and check moving parts like the spring on secateurs. A drop of oil there keeps them opening and closing smoothly.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - cleaning hand tools', url: 'https://www.rhs.org.uk/videos/advice/grow-at-home/winter-tool-care' },
    ],
  },
  {
    slug: 'lawn-care-calendar',
    title: 'Lawn Care Calendar',
    icon: '🌾',
    summary: 'When to scarify, aerate, feed and reseed a South of England lawn through the year.',
    category: 'Lawn',
    sections: [
      {
        heading: 'Spring (March to May)',
        body: [
          'Start mowing regularly once the grass begins to grow. Lower the cutting height a little at a time over several mows, rather than cutting it very short in one go. This keeps the lawn strong and helps stop weeds taking hold.',
          'Spring is also a good time to aerate or lightly scarify (rake out moss and dead grass) if you didn\u2019t get round to it in autumn. In the South of England, cool, damp spring weather helps the lawn recover quickly. Don\u2019t work the lawn while it\u2019s waterlogged, as this can do more harm than good.',
          'Put down a spring or summer lawn feed once the grass is growing well, to keep the colour strong and even.',
        ],
      },
      {
        heading: 'Summer (June to August)',
        body: [
          'Keep mowing regularly, but raise the cutting height in dry spells. Longer grass shades its own roots and copes with dry weather much better than a lawn cut very short.',
          'A lawn is tougher than it looks. Letting the grass go brown in a dry summer is usually better than heavy watering, which is rarely needed for an ordinary garden lawn here.',
        ],
      },
      {
        heading: 'Autumn (September to October) - the main job of the year',
        body: [
          'Autumn is the best time for lawn work in the UK. The soil is still warm enough for quick recovery, there\u2019s more rain than in summer, and weeds are dying back.',
          'Scarify the lawn - rake out the thatch, which is the build-up of dead grass, moss and other debris at the base of the grass. This lets water, air and food reach the roots.',
          'Aerate any compacted areas afterwards to help drainage, especially on the heavier clay soil found in parts of the South of England.',
          'Reseed any thin or bare patches straight after scarifying and aerating, while the ground is open, then put down an autumn lawn feed.',
        ],
      },
      {
        heading: 'Winter (November to February)',
        body: [
          'Growth slows right down. Only mow now and then, on dry days, with the blades set high, and only if the grass is actually growing.',
          'Stay off the lawn when it\u2019s waterlogged or frosty, as both wet, soft ground and frozen grass are easily damaged by walking on them.',
          'Winter is a good time to sort out drainage on a lawn that floods after rain, as it\u2019s easiest to see the problem areas when the ground is at its wettest.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - lawn care in autumn', url: 'https://www.rhs.org.uk/lawns/autumn-care' },
      { label: 'RHS - lawn care in spring and summer', url: 'https://www.rhs.org.uk/lawns/spring-summer-care' },
      { label: 'RHS - waterlogged lawns', url: 'https://www.rhs.org.uk/lawns/waterlogging' },
    ],
  },
  {
    slug: 'common-plant-diseases',
    title: 'Common Plant Diseases',
    icon: '🔍',
    summary: 'How to spot and deal with the plant problems most likely to turn up on roses, fruit and vegetables in a South of England garden.',
    category: 'Problems',
    sections: [
      {
        heading: 'Why our climate matters here',
        body: [
          'Many of the diseases below like warm, damp, wet conditions - which is exactly what a mild South of England summer often gives us, more so than cooler, drier parts of the UK. Good airflow, and keeping leaves dry, matter more here than further north.',
          'Healthy soil also helps. Research from the University of Reading\u2019s School of Agriculture, Policy and Development on soil and plant disease backs up what gardeners have long believed by instinct: good, healthy soil grows stronger plants, and a stressed plant gets ill more easily.',
        ],
      },
      {
        heading: 'Rose black spot',
        body: [
          'The most serious disease of roses in UK gardens. Look for black or purple patches on the leaves that spread quickly, often with a yellow ring around them, and leaves that drop off even when the rest of the plant looks fine.',
          'The fungus needs water sitting on the leaf to take hold, so water the base of the plant rather than the leaves, and give plants enough space for air to move around them.',
          'Rake up fallen leaves and bin them rather than composting them. The fungus survives over winter in old leaves and infects the plant again the following spring.',
        ],
      },
      {
        heading: 'Powdery mildew',
        body: [
          'Affects lots of plants, including roses, courgettes, cucumbers and many garden flowers. Look for a white, dusty coating on leaves, stems and buds.',
          'Unlike most plant diseases, powdery mildew is actually worse in dry weather combined with high humidity and poor airflow - a common mix in a sheltered, crowded border in summer.',
          'Help by thinning out crowded growth for better airflow, watering the base rather than the leaves, and removing badly affected leaves quickly so it doesn\u2019t spread further.',
        ],
      },
      {
        heading: 'Tomato and potato blight',
        body: [
          'A serious disease that affects both tomatoes and potatoes. It likes the same mild, wet, humid weather we often get in a South of England summer.',
          'Look for dark patches on leaves and stems that spread fast, often with a lighter ring around them, followed by brown, rotten patches on the fruit or tubers.',
          'Outdoor tomatoes are most at risk in a wet summer. Growing them under cover, in a greenhouse or polytunnel, helps a lot. If you spot blight, remove and bin the affected parts straight away rather than composting them.',
        ],
      },
      {
        heading: 'Aphids (greenfly and blackfly)',
        body: [
          'Not a disease, but worth knowing about too. Aphids gather on the underside of leaves and on new growth. Their sticky waste, called honeydew, often leads to a black sooty mould growing on the leaves below.',
          'A few aphids are normal and are usually kept in check by other insects. Check under leaves often, so a small problem can be dealt with early with a strong jet of water, or by squashing them, before it becomes a big one.',
          'Bringing ladybirds, lacewings and hoverflies into the garden helps a lot - marigolds are a well-known way to attract them. This gives you an ongoing, easy way to keep aphids down without spraying.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - rose black spot', url: 'https://www.rhs.org.uk/disease/rose-black-spot/' },
      { label: 'David Austin Roses - the truth about black spot', url: 'https://davidaustinroses.co.uk/blogs/rose-care/the-truth-about-blackspot-a-guide-to-keeping-your-roses-healthy' },
      { label: 'RHS - powdery mildews', url: 'https://www.rhs.org.uk/disease/powdery-mildews' },
      { label: 'RHS - tomato blight', url: 'https://www.rhs.org.uk/disease/tomato-blight/' },
      { label: 'RHS - aphids', url: 'https://www.rhs.org.uk/biodiversity/aphids' },
      { label: 'University of Reading - School of Agriculture, Policy and Development (soil and plant disease research)', url: 'https://www.reading.ac.uk/apd/research' },
    ],
  },
  {
    slug: 'drip-irrigation-water-saving',
    title: 'Drip Irrigation and Saving Water',
    icon: '💧',
    summary: 'Why a drip system beats a hose or sprinkler, and how to keep the garden going through longer, hotter dry spells.',
    category: 'Watering',
    sections: [
      {
        heading: 'Why summers here are getting harder on the garden',
        body: [
          'Long dry spells and hosepipe bans are becoming a more regular part of the South of England summer, not a one-off. Getting set up to water efficiently now, rather than scrambling for a bucket mid-heatwave, makes a real difference.',
          'A sprinkler is by far the least efficient way to water. It can use as much water in an hour as a family of four normally uses in two days, and much of that is lost to evaporation and wind before it ever reaches the roots.',
        ],
      },
      {
        heading: 'Why drip irrigation is worth setting up',
        body: [
          'A drip or micro-irrigation system delivers water slowly, straight to the base of each plant, so very little is lost to evaporation, wind drift or run-off compared with a sprinkler or an overhead hose.',
          'Gardena, who make a popular micro-drip system for home gardens, put the water saving at up to 70% compared with a normal hose or sprinkler. That matches the wider picture from drip irrigation generally, which keeps the soil surface between plants drier too, which in turn means fewer weeds.',
          'Because the system is fixed in place with individual drippers or micro-tubes, it can be set to a timer, so containers and beds get watered evenly even when you\u2019re not around - handy if you\u2019re away, or simply out at work during the hottest part of the day.',
        ],
      },
      {
        heading: 'Setting up a system like this',
        body: [
          'A kit such as the Gardena Micro-Drip-System is built from a supply pipe with individual micro-tubes and drippers you position at each plant or pot, so you can build it out to fit your own beds, borders and containers rather than being stuck with a fixed layout.',
          'Run it from a water butt where you can, rather than the mains, especially if a hosepipe ban is in place - most bans still allow watering by hand from a stored supply such as a butt.',
          'Check drippers occasionally for blockages, particularly if your water is hard, and adjust the flow at each dripper so thirstier plants (like courgettes or newly planted anything) get more than established shrubs.',
        ],
      },
      {
        heading: 'Everyday habits that save water alongside a drip system',
        body: [
          'Water early in the morning or in the evening, never in the heat of the day, so less is lost to evaporation before it soaks in.',
          'Water deeply but less often, rather than a light sprinkle every day - this trains roots to grow deeper, where the soil stays moist for longer.',
          'Mulch beds and borders with compost, bark or well-rotted manure to lock moisture into the soil and cut down on weeds competing for it. Water well before mulching, not after, so the moisture is sealed in rather than out.',
          'Collect rainwater in a water butt whenever you can, and keep a watering can in the bathroom to catch grey water, such as water run off before a shower warms up.',
          'Choose plants suited to a dry spell where you can. Established trees, shrubs and lawns are usually tougher than they look - a lawn left to go brown in a drought will nearly always green up again once normal rain returns.',
        ],
      },
    ],
    furtherReading: [
      { label: 'RHS - garden watering methods', url: 'https://www.rhs.org.uk/garden-jobs/garden-watering-methods' },
      { label: 'RHS - how to reduce water use during a heatwave', url: 'https://www.rhs.org.uk/science/gardening-in-a-changing-world/water-use-in-gardens/reduce-water-use-during-heatwave' },
      { label: 'Gardena - Micro-Drip-System', url: 'https://www.gardena.com/uk/products/watering/micro-drip-system' },
    ],
  },
];

export default guides;

export function getGuideBySlug(slug) {
  return guides.find(g => g.slug === slug);
}
